import * as React from 'react';
import s from '../../RequestCall.scss';
import { Formik } from 'formik';
import { validateValues } from './FormValidation';
import { FormContext, formInitialData } from './FormContext';
import { FormInput } from './FormInput';
import { FormInputArea } from './FormInputArea';
import { FormProps, FormikRenderProps, FormCompound } from './FormTypes';
import Api from '../../../../services/Api/Api';
import { withInjectedRootStore } from '../../../../providers';
import { Layout } from 'wix-style-react';
import { withExperiments } from '@wix/wix-experiments-react';
import {
  Experiments,
  RequestCallSource,
  PlanType,
  Theme,
} from '../../../../enums';
import classNames from 'classnames';
import { usePageModeContext } from '../../../../providers/PageMode/PageMode.provider';

const themePlanTypeSourceMap = {
  [Theme.AdvancedPlans]: {
    [PlanType.Website]: RequestCallSource.AdvancedPlansWebsiteBanner,
    [PlanType.Business]: RequestCallSource.AdvancedPlansBusinessPlan,
    [PlanType.Enterprise]: RequestCallSource.AdvancedPlansEnterprisePlan,
  },
  [Theme.EditorX]: {
    [PlanType.Website]: RequestCallSource.EditorXWebsiteBanner,
    [PlanType.Business]: RequestCallSource.EditorXBusinessPlan,
    [PlanType.Enterprise]: RequestCallSource.EditorXEnterpriseBanner,
  },
};

export const FormComp: React.FC<FormProps> = ({
  children,
  onSubmitSuccess,
  rootStore,
  experiments,
}) => {
  const [submitFailureMessage, setSubmitFailureMessage] = React.useState('');
  const [isDirty, setIsDirty] = React.useState(false);

  const {
    appContext: { biLogger, locale, country, referral, userGuid },
    planType,
  } = rootStore.appStore;

  const { theme } = usePageModeContext();

  const getSource = () =>
    themePlanTypeSourceMap[theme][planType] ?? RequestCallSource.Default;

  const onSubmitCallback = (values, { setSubmitting }) => {
    setSubmitting(true);
    biLogger.logRequestCallPopupCTA({ clickType: 'submit' });
    const apiEndpoint = Api.RequestCallFormSubmit_V2;

    apiEndpoint(
      { ...values, locale, country, referral, userGuid, source: getSource() },
      experiments.enabled(Experiments.useRequestCallProxyAPI),
    )
      .then(() => {
        setSubmitting(false);
        onSubmitSuccess(true);
      })
      .catch(() => {
        setSubmitting(false);
        setSubmitFailureMessage(
          'enterprise_plans.request_a_call.notification.general_error',
        );
      });
  };

  const handleSubmitForm = (e, handleSubmit) => {
    e && e.preventDefault();
    !isDirty && setIsDirty(true);
    handleSubmit();
  };
  const isEnterpriseTab = experiments.enabled(Experiments.AddEnterpriseTab);

  return (
    <Formik
      validateOnBlur={false}
      validateOnChange={isDirty}
      initialValues={formInitialData}
      validate={(values) => validateValues(values)}
      onSubmit={onSubmitCallback}
    >
      {({ handleSubmit, ...formikRenderProps }: FormikRenderProps) => (
        <FormContext.Provider
          value={{ submitFailureMessage, handleSubmit, ...formikRenderProps }}
        >
          <form
            data-hook='contact-details-form'
            onSubmit={(e) => handleSubmitForm(e, handleSubmit)}
          >
            <div
              className={classNames(s.formWrapper, {
                [s.enterprise]: isEnterpriseTab,
              })}
            >
              <Layout gap='20px'>{children}</Layout>
            </div>
          </form>
        </FormContext.Provider>
      )}
    </Formik>
  );
};

export const Form = withExperiments(
  withInjectedRootStore(FormComp),
) as React.FC<Omit<FormProps, 'rootStore' | 'experiments'>> & FormCompound;
Form.Input = FormInput;
Form.InputArea = FormInputArea;
